import { cn } from "../../lib/utils";

/**
 * Trigger geometry for every "this opens" control in the app.
 *
 * `docs/picker-taxonomy.md` §2. The single anti-copy-paste artifact: pure
 * strings and one constant, no React. A `Select` trigger (Job A) and a
 * `Picker` trigger (Job B) in the same context must be pixel-identical, which
 * is only enforceable if they are produced by one function.
 *
 * Two rules that look like details and are not:
 *
 * - The only "this opens" glyph is a downward caret. `ChevronsUpDown` is
 *   banned — it was the visible tell that two identical-looking controls were
 *   different mechanisms.
 * - The focus ring is `` at **full opacity**, never ``.
 *   At 50% the ring measures ≈2.9:1 against `bg-page` in light theme and
 *   fails the 3:1 non-text floor; at full opacity it is 8.4:1 light and
 *   6.6:1 dark. `focus-visible`, not `focus`, so a mouse click leaves nothing
 *   behind.
 */

/**
 * The Job A / Job B boundary (§1.1). At or below this many options a listbox
 * shows the whole choice at once (12 × `h-control` + a group eyebrow + `p-1`
 * = 412px, inside `SelectContent`'s `max-h-104`); past it the user needs a
 * visible substring query, which is a combobox.
 *
 * Generic renderers that do not know their option count at author time apply
 * it at runtime:
 *
 * ```tsx
 * options.length > PICKER_THRESHOLD
 *   ? <Picker variant="inline" options={options} … />
 *   : <Select …><SelectTrigger variant="inline">…</SelectTrigger>…</Select>
 * ```
 *
 * The count is only half the rule — a list populated from workspace records
 * (people, projects, tasks, milestones, permission roles, labels) is Job B
 * regardless of how many rows the seeded tenant has today.
 */
export const PICKER_THRESHOLD = 12;

export type TriggerVariant = "field" | "inline" | "compact" | "nav";

/** Present in all four variants (§2.1). */
const PICKER_TRIGGER_BASE = [
  "flex min-w-0 items-center gap-1.5 rounded-sm text-left",
  "transition-[background-color,color] duration-100 ease-out-fast",
  "focus-visible:outline-none focus-fill",
  "disabled:cursor-not-allowed disabled:opacity-50",
  "data-[placeholder]:text-text-tertiary",
  "[&_svg]:shrink-0",
].join(" ");

const PICKER_TRIGGER_VARIANTS: Record<TriggerVariant, string> = {
  /**
   * Forms, dialogs, settings panels. The default.
   *
   * Width is always `w-full`; the **parent** constrains it
   * (`<div className="w-32">`), never the trigger. That is what retires the
   * hand-written `w-24`/`w-28`/`w-[180px]`/`w-[62px]` widths in the audit.
   *
   * A filled well, not an outlined box (`docs/borderless-contract.md` §1, §4):
   * the edge that used to say "you can type/choose here" is now the `bg-field`
   * fill, and `hover:border-line-strong` becomes `hover:bg-field-strong`.
   * Height and padding are unchanged, so a `Select` trigger and a `Picker`
   * trigger remain pixel-identical and still line up with `Input`.
   *
   * `bg-field`, not `bg-sunken` — the same token `Input` and `Textarea` take.
   * A trigger is a `<button>`, so an app that re-points its control fill with
   * a rule over the real form elements (opero-app's unlayered
   * `input, textarea, select`) never reached this one, and its dropdowns sat
   * on a different colour from the text fields beside them. `--color-field`
   * aliases `--color-sunken`, so the two are one colour unless an app moves
   * them.
   */
  field: [
    "h-control-lg w-full justify-between rounded-md px-4",
    "bg-field text-sm text-text",
    "hover:bg-field-strong",
  ].join(" "),

  /**
   * Property rows, inline click-to-edit, detail rails. Borderless at rest;
   * the caret is always visible and never hover-gated, because a control
   * whose affordance only appears on hover is invisible to touch and to
   * anyone scanning the rail.
   */
  inline: [
    "h-control w-full justify-between rounded-sm px-2",
    "bg-transparent text-sm text-text",
    "hover:bg-hover",
    "data-[state=open]:bg-selected",
  ].join(" "),

  /**
   * 36px toolbars, the 40px task header bar, dialog chip rows, dense table
   * cells. `text-text-secondary` is 7.0:1 light / 10.0:1 dark at 11px and the
   * `text-text-tertiary` caret is 4.98:1 / 6.9:1, clearing the 3:1 glyph
   * floor in both themes.
   */
  compact: [
    "inline-flex h-7 max-w-full shrink-0 items-center gap-1.5 rounded-sm px-2",
    "bg-transparent text-xs font-medium text-text-secondary",
    "hover:bg-hover hover:text-text",
    "data-[state=open]:bg-selected data-[state=open]:text-text",
  ].join(" "),

  /**
   * The two sidebar identity rows — the workspace switcher at the top of the
   * rail and the account menu at the bottom (§7). They are the same physical
   * affordance as a value trigger, so they share its geometry deliberately.
   *
   * Both the background *and* the label colour move on open; the account row
   * previously darkened while its label stayed `text-text-secondary`, so
   * "open" read as less active than "hover".
   */
  nav: [
    "flex h-control min-w-0 items-center gap-2 rounded-sm px-2",
    "text-sm text-text",
    "transition-[background-color,color] duration-100 ease-out-fast",
    "hover:bg-hover hover:text-text",
    "data-[state=open]:bg-selected data-[state=open]:text-text",
    "focus-visible:outline-none focus-fill",
  ].join(" "),
};

/**
 * Job A (`Select`), Job B (`Picker`) and Job D (`DatePicker`, range panels)
 * trigger geometry (§2.1).
 *
 * The caret is rendered by the component, not by the call site: `size-3.5` for
 * `field`/`inline`, `size-3` for `compact`, always `text-text-tertiary`.
 * Job D triggers pass their domain glyph (calendar, swatch) and no caret.
 */
export function pickerTriggerClass(
  variant: TriggerVariant = "field",
  className?: string,
): string {
  return cn(PICKER_TRIGGER_BASE, PICKER_TRIGGER_VARIANTS[variant], className);
}

/**
 * Job C toolbar trigger (§2.7) — "+ Filter", the filter panels, data-table
 * column controls.
 *
 * The `data-[state=open]` pair is the whole delta from the old
 * `AddFilterButton` string: without it the affordance renders at rest while
 * its own panel is open.
 */
const MENU_TRIGGER_COMPACT = [
  "inline-flex h-7 shrink-0 items-center gap-1 rounded-sm px-2",
  "text-xs text-text-tertiary",
  "transition-[background-color,color] duration-100 ease-out-fast",
  "hover:bg-hover hover:text-text",
  "data-[state=open]:bg-selected data-[state=open]:text-text",
  "focus-visible:outline-none focus-fill",
  // §2.0-6 applies to every trigger in every job, not only the value ones —
  // this was the one recipe missing it.
  "disabled:cursor-not-allowed disabled:opacity-50",
  "[&_svg]:size-3 [&_svg]:shrink-0",
].join(" ");

/**
 * Job C trigger geometry. `"compact"` is a toolbar affordance (§2.7);
 * `"nav"` is a sidebar identity row and is **identical** to
 * `pickerTriggerClass("nav")` by design (§2.5).
 *
 * Job C *icon* triggers are not covered here — they are
 * `<DropdownMenuTrigger asChild><IconButton …/></DropdownMenuTrigger>` (§2.6).
 */
export function menuTriggerClass(
  variant: "compact" | "nav" = "compact",
  className?: string,
): string {
  if (variant === "nav") return pickerTriggerClass("nav", className);
  return cn(MENU_TRIGGER_COMPACT, className);
}
